// SKILL.md assembly + fingerprint helpers for workspace skill authoring.
//
// The authoring form keeps name/description/categories/tags/permissions/examples/
// dependencies as structured state (see workspace-sections/skill-section.jsx) and
// only lets the user hand-write the markdown BODY. assembleSkillMarkdown() is the
// single place that combines all of that into the final file at publish time —
// there is no "read fields back out of existing text" step anymore, so unlike an
// earlier version of this file there's nothing to keep in sync/re-parse.

export function buildFingerprint({ host, major, endpointKeys }) {
  return {
    host: String(host || ''),
    major: Number(major) || 1,
    endpointKeys: [...(endpointKeys || [])].map(String).sort(),
  };
}

export function fingerprintsEqual(a, b) {
  if (!a || !b) return false;
  return (
    String(a.host || '') === String(b.host || '') &&
    Number(a.major) === Number(b.major) &&
    JSON.stringify([...(a.endpointKeys || [])].map(String).sort()) ===
      JSON.stringify([...(b.endpointKeys || [])].map(String).sort())
  );
}

export function bumpPatchVersion(version) {
  const m = String(version || '0.1.0').match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!m) return '0.1.0';
  return `${m[1]}.${m[2]}.${Number(m[3]) + 1}`;
}

/** Body-only starter template — seeds a brand-new/unbound skill's editor. No frontmatter. */
export function buildScaffoldBody({ apiName, description, hostPrefix, gatewayUrl, endpoints = [] }) {
  const service = hostPrefix || 'my-api';
  const gateway = gatewayUrl || `https://${service}.p.xapi.to`;
  const epList =
    endpoints.length === 0
      ? '- _(no public endpoints yet)_'
      : endpoints
          .map(
            (ep) =>
              `- \`${ep.method} ${ep.path}\` — ${ep.name || ep.path}${
                ep.description ? `: ${ep.description}` : ''
              }`,
          )
          .join('\n');
  const samplePath = endpoints[0]?.path || '/v1/example';

  return `# ${apiName || service}

${description || `Agent skill for calling **${apiName || service}** through the xapi gateway.`}

## Auth

\`\`\`bash
export XAPI_KEY=xapi_...
\`\`\`

Gateway: \`${gateway}\`.

## Endpoints

${epList}

## Example

\`\`\`bash
curl -sS "${gateway}${samplePath}" \\
  -H "xapi-key: $XAPI_KEY"
\`\`\`
`;
}

// metadata.xapi.dependencies[].purpose is z.string().min(20).max(300) server-side
// (skill-package.validator.ts XAPI_METADATA_DEPENDENCY_SCHEMA) — most endpoint
// names/descriptions are shorter than that (e.g. "posts"), so a bare copy fails
// validation on first submit. Pad with boilerplate until it clears the floor.
export function padSkillDependencyPurpose(text) {
  const base = String(text || '').trim() || 'this endpoint';
  if (base.length >= 20) return base.slice(0, 300);
  return `${base} — call via the xapi gateway`.slice(0, 300);
}

function buildFrontmatterYaml({
  name,
  description,
  hostPrefix,
  endpoints = [],
  categories = [],
  tags = [],
  permissions = {},
  examples = [],
}) {
  const service = hostPrefix || 'my-api';

  const deps =
    endpoints.length === 0
      ? `      - service: ${service}\n        required: true\n        purpose: Call this API via xapi gateway`
      : endpoints
          .map((ep) => {
            const key =
              ep.endpointKey ||
              `${String(ep.method || 'get').toLowerCase()}-${String(ep.path || '').replace(/^\//, '')}`;
            const purpose = JSON.stringify(
              padSkillDependencyPurpose(
                ep.description || ep.name || `${ep.method} ${ep.path}`,
              ),
            );
            return `      - service: ${service}
        endpoint: ${key}
        required: true
        purpose: ${purpose}`;
          })
          .join('\n');

  const lines = ['---', `name: ${JSON.stringify(name)}`, `description: ${JSON.stringify(description || name)}`, 'metadata:', '  xapi:'];

  if (categories.length) {
    lines.push('    categories:');
    for (const c of categories) lines.push(`      - ${JSON.stringify(c)}`);
  }
  if (tags.length) {
    lines.push('    tags:');
    for (const t of tags) lines.push(`      - ${JSON.stringify(t)}`);
  }
  lines.push('    dependencies:', deps);
  lines.push(
    '    permissions:',
    `      externalWrites: ${!!permissions.externalWrites}`,
    `      spendsCredits: ${!!permissions.spendsCredits}`,
    `      personalData: ${!!permissions.personalData}`,
  );
  if (examples.length) {
    lines.push('    examples:');
    for (const ex of examples) {
      lines.push(`      - title: ${JSON.stringify(ex.title || '')}`);
      lines.push(`        prompt: ${JSON.stringify(ex.prompt || '')}`);
    }
  }
  lines.push('---');
  return lines.join('\n');
}

/**
 * Combine structured form state (name/description/categories/tags/permissions/
 * examples/dependencies) with the user's body markdown into the final SKILL.md
 * file. Replaces the old buildFrontmatter() + mergeSkillDepsIntoMarkdown() pair —
 * every field is freshly derived from live state each time, so there's no more
 * "extract from existing text, then splice" round trip to keep in sync.
 */
export function assembleSkillMarkdown({
  name,
  description,
  hostPrefix,
  endpoints = [],
  categories = [],
  tags = [],
  permissions = {},
  examples = [],
  bodyMarkdown = '',
}) {
  const frontmatter = buildFrontmatterYaml({
    name,
    description,
    hostPrefix,
    endpoints,
    categories,
    tags,
    permissions,
    examples,
  });
  return `${frontmatter}\n\n${String(bodyMarkdown || '').replace(/^\n+/, '')}`;
}

/** Build a browser File (zip) containing a single SKILL.md for uploadZip. */
export async function skillMdToZipFile(skillMarkdown, filename = 'skill.zip') {
  const { zipSync, strToU8 } = await import('fflate');
  const zipped = zipSync(
    {
      'SKILL.md': strToU8(String(skillMarkdown || '')),
    },
    { level: 6 },
  );
  return new File([zipped], filename, { type: 'application/zip' });
}
